import {
  Menu,
  MenuItem,
  Box,
  Typography,
  Divider,
  Badge,
  Avatar,
  alpha,
  useTheme,
  Tooltip,
  useMediaQuery,
  Switch
} from '@mui/material';
import { useDispatch, useSelector } from "react-redux";
import { toggleFunMode } from '../../../../redux/fun-mode/funModeSlice';
import { USER_MENU_ITEMS } from '../config/navConfig';

const UserMenu = ({
  anchorElUser,
  handleCloseUserMenu,
  handleOpenUserMenu,
  userInfo,
  hasPost,
  currentPath
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const isFunMode = useSelector((state) => state.funMode.isFunMode);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
      {/* <Tooltip title="Notifications">
        <IconButton
          sx={{
            color: 'white',
            fontSize: '1.75rem',
            transition: 'box-shadow 0.3s ease, transform 0.2s ease',
            '&:hover': {
              boxShadow: `0 0 8px 2px ${alpha(theme.palette.secondary.main, 0.6)}`,
              transform: 'translateY(-2px)',
            },
          }}
        >
          <Badge
            badgeContent={userInfo?.notifications || 0}
            color="error"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.75rem',
                height: 18,
                minWidth: 18,
              },
            }}
          >
            <NotificationsIcon fontSize="inherit" />
          </Badge>
        </IconButton>
      </Tooltip> */}
      <Tooltip title={userInfo?.name || "User profile"}>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          variant="dot"
          color={userInfo?.isOnline ? 'success' : 'default'}
        >
          <Avatar
            alt={userInfo?.name || "User"}
            src={userInfo?.profilePic?.url || "/default-avatar.png"}
            onClick={handleOpenUserMenu}
            sx={{
              width: { xs: 36, md: 42 },
              height: { xs: 36, md: 42 },
              border: '2px solid',
              borderColor: userInfo?.isOnline ? 'success.main' : 'secondary.main',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: `0 0 0 2px ${alpha(theme.palette.secondary.main, 0.3)}`,
              }
            }}
          />
        </Badge>
      </Tooltip>

      <Menu
        elevation={6}
        sx={{
          mt: '10px',
          '& .MuiPaper-root': {
            minWidth: 250,
            maxHeight: '50vh',
            overflow: 'auto',
            py: 0,
            px: 0,
            borderRadius: 2,
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
            mt: 0.75,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          }
        }}
        anchorEl={anchorElUser}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(anchorElUser)}
        onClose={() => handleCloseUserMenu(null)}
      >
        {/* User Header */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={userInfo?.profilePicture || "/default-avatar.png"}
              sx={{ width: 40, height: 40 }}
            />
            <Box>
              <Typography variant="subtitle2" fontWeight="medium">
                {userInfo?.name || "User"}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 180 }}>
                {userInfo?.email || ""}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* Normal Menu Items */}
        {USER_MENU_ITEMS(hasPost).map((item) => {
          const isActive = currentPath === item.path;
          if (item.name === "Logout") {
            return null; // we'll render Logout after Fun Mode
          }

          return (
            <Box key={item.name}>
              {item.divider && <Divider />}
              <MenuItem
                onClick={() => handleCloseUserMenu(item)}
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 0,
                  backgroundColor: isActive ? alpha(theme.palette.secondary.main, 0.1) : 'transparent',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    color: isActive ? 'primary.main' : 'text.primary',
                    fontWeight: isActive ? 600 : 500,
                  }}
                >
                  {item.icon}
                  {item.name}
                </Box>
              </MenuItem>
            </Box>
          );
        })}

        {/* Fun Mode Toggle */}
        <Divider />
        <MenuItem sx={{ py: 1, px: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <Typography variant="body2" fontWeight={500}>
              Fun Mode
            </Typography>
            <Switch
              checked={isFunMode}
              onChange={() => dispatch(toggleFunMode())}
              color="secondary"
            />
          </Box>
        </MenuItem>

        {/* Logout */}
        <Divider />
        <MenuItem
          onClick={() => handleCloseUserMenu({ name: "Logout" })}
          sx={{
            py: 1,
            px: 2,
            borderRadius: 0,
            '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.08) }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#f44336', fontWeight: 600 }}>
            {USER_MENU_ITEMS(hasPost).find(i => i.name === "Logout")?.icon}
            Logout
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserMenu;
